import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { useSaveKey, withSaveKey } from "@/lib/saveOnce";
import { useFillOnOpen } from "@/lib/fillOnOpen";
import { PageHeader, Card, EmptyState, Btn } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, GraduationCap, Info, Warning } from "@phosphor-icons/react";
import { ViewButton, RecordView } from "@/components/RecordView";
import { scholarshipFields, RECORD_TITLES } from "@/lib/recordFields";
import { fmtDate, money } from "@/lib/format";
import { MoneyInput } from "@/lib/accountingFormat";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { useDateErrors } from "@/lib/useDateErrors";
import { toast } from "sonner";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { NotesField } from "@/components/NotesField";

const SOURCE_TYPES = [
  { key: "scholarship", label: "Scholarship" },
  { key: "grant", label: "Grant" },
  { key: "veterans_assistance", label: "Veterans assistance" },
  { key: "employer_assistance", label: "Employer assistance" },
  { key: "other_tax_free", label: "Other tax-free" },
];

export default function Scholarships() {
  const qc = useQueryClient();
  const { selectedStudentId } = useAuth();
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: scholarships = [] } = useQuery({
    queryKey: ["scholarships", selectedStudentId],
    queryFn: async () => (await api.get(`/scholarships${selectedStudentId ? `?student_id=${selectedStudentId}` : ""}`)).data,
  });
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  // The scholarship behind the eye — read only. (ZEN-702)
  const [viewing, setViewing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["scholarships"] }); qc.invalidateQueries({ queryKey: ["dashboard-tax-year"] }); };
  const nameOf = (id) => students.find((s) => s.id === id)?.full_name || "—";
  const del = async (id) => { await api.delete(`/scholarships/${id}`); toast.success("Deleted"); refresh(); setDelTarget(null); };

  const byYear = {};
  for (const s of scholarships) {
    const yr = s.tax_year || 0;
    if (!byYear[yr]) byYear[yr] = [];
    byYear[yr].push(s);
  }
  const years = Object.keys(byYear).sort((a, b) => b - a);

  return (
    <div>
      <PageHeader title="Scholarships & Aid" subtitle="Tax-free educational assistance reduces Adjusted Qualified Education Expenses (AQEE) per IRS Pub 970."
        actions={<Btn onClick={() => { setEditing(null); setDlg(true); }} data-testid="add-scholarship-btn"><Plus size={16} weight="bold" /> Add scholarship/aid</Btn>} />

      <div className="mb-3 flex items-start gap-2 rounded-lg border border-[#C2C9D1] bg-[#F0F6FF] px-3 py-2.5 text-xs text-[#475569]" data-testid="scholarships-info-banner">
        <Info size={16} weight="bold" className="mt-0.5 shrink-0 text-[#034AA2]" />
        <span>Only <b>tax-free</b> scholarships, grants, and educational assistance reduce your Adjusted Qualified Education Expenses (AQEE). A scholarship is tax-free only for tuition, fees, and required course materials &mdash; not room and board. If a scholarship isn&rsquo;t restricted to tuition, you can choose to apply part of it to room and board: that part becomes taxable to the beneficiary, but it keeps more expenses available for a tax-free 529 withdrawal. To record that choice today, enter that portion as a separate entry with <b>Tax-free</b> turned off. <em>IRS Publication 970, chapter 7.</em></span>
      </div>

      {scholarships.length === 0 ? (
        <EmptyState icon={GraduationCap} title="No scholarships or aid yet" subtitle="Add scholarships, grants, or tax-free assistance to compute AQEE." action={<Btn onClick={() => setDlg(true)}><Plus size={16} weight="bold" /> Add scholarship/aid</Btn>} />
      ) : (
        <div className="space-y-6">
          {years.map((yr) => (
            <div key={yr}>
              <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Tax Year {yr}</h3>
              <AqeeSummary studentId={selectedStudentId} taxYear={parseInt(yr)} students={students} />
              <div className="mt-2 grid grid-cols-1 gap-3 lg:grid-cols-2">
                {byYear[yr].map((s) => (
                  <Card key={s.id} data-testid={`scholarship-${s.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 font-bold">
                          <GraduationCap size={18} weight="bold" className="shrink-0 text-[#034AA2]" />
                          <span className="truncate">{s.label}</span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-[#6b7280]">
                          <span>{nameOf(s.student_id)}</span>
                          <span className="rounded-full bg-[#E9E9EB] px-2 py-0.5 font-semibold">{SOURCE_TYPES.find((t) => t.key === s.source_type)?.label || s.source_type}</span>
                          {s.tax_free ? <span className="rounded-full bg-[#077A39]/10 px-2 py-0.5 font-semibold text-[#077A39]">Tax-free</span> : <span className="rounded-full bg-[#D97706]/10 px-2 py-0.5 font-semibold text-[#D97706]">Not tax-free</span>}
                          {s.date_received && <span>{fmtDate(s.date_received)}</span>}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        {/* The eye, immediately before the pencil. (ZEN-702) */}
                        <ViewButton onClick={() => setViewing(s)} testid={`scholarship-view-${s.id}`} size={15} />
                        <button onClick={() => { setEditing(s); setDlg(true); }} className="rounded p-1.5 text-[#034AA2] hover:bg-[#E9E9EB]" data-testid={`scholarship-edit-${s.id}`}><PencilSimple size={15} weight="bold" /></button>
                        <button onClick={() => setDelTarget(s.id)} className="rounded p-1.5 text-[#F01919] hover:bg-[#E9E9EB]" data-testid={`scholarship-del-${s.id}`}><Trash size={15} weight="bold" /></button>
                      </div>
                    </div>
                    <div className="mt-2 text-xl font-extrabold tabular-nums">{money(s.amount, "USD")}</div>
                    {s.notes && <div className="mt-1 text-xs text-[#6b7280]">{s.notes}</div>}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ScholarshipDialog open={dlg} onClose={() => setDlg(false)} editing={editing} students={students} defaultStudent={selectedStudentId} onSaved={refresh} />
      <RecordView
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={RECORD_TITLES.scholarship}
        fields={viewing ? scholarshipFields(viewing, { studentName: nameOf(viewing.student_id), sourceLabel: SOURCE_TYPES.find((t) => t.key === viewing.source_type)?.label }) : []}
        testid="scholarship-view"
      />
      <DeleteConfirmDialog open={!!delTarget} onClose={() => setDelTarget(null)} onConfirm={() => del(delTarget)}
        title="Delete this scholarship?" description="This will affect AQEE calculations." />
    </div>
  );
}


function AqeeSummary({ studentId, taxYear }) {
  const { data: dashboard } = useQuery({
    queryKey: ["dashboard-tax-year", taxYear, studentId],
    queryFn: async () => (await api.get(`/dashboard/tax-year/${taxYear}${studentId ? `?student_id=${studentId}` : ""}`)).data,
    enabled: !!taxYear,
  });
  if (!dashboard?.students?.length) return null;

  return (
    <div className="space-y-2">
      {dashboard.students.map(({ student, aqee }) => {
        if (!aqee) return null;
        const showTaxable = aqee.taxable_earnings != null && aqee.taxable_earnings > 0;
        return (
          <div key={student.id} className="rounded-lg border border-[#C2C9D1] bg-white px-4 py-3" data-testid={`aqee-summary-${student.id}-${taxYear}`}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="font-bold text-[#0A0A0A]">{student.full_name}</span>
              <span className="text-[#6b7280]">Qualified: <b className="text-[#0A0A0A]">{money(aqee.total_qualified, "USD")}</b></span>
              {aqee.tax_free_assistance > 0 && <span className="text-[#D97706]">Less aid: <b>{money(aqee.tax_free_assistance, "USD")}</b></span>}
              {aqee.aotc_llc_reserved > 0 && <span className="text-[#D97706]">Less AOTC reserve: <b>{money(aqee.aotc_llc_reserved, "USD")}</b></span>}
              <span className={`font-extrabold ${aqee.tax_free_assistance > 0 || aqee.aotc_llc_reserved > 0 ? "text-[#D97706]" : "text-[#077A39]"}`}>
                AQEE: {money(aqee.aqee, "USD")}
              </span>
            </div>
            {aqee.has_1099q && (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="text-[#6b7280]">1099-Q: dist {money(aqee.box1_gross_distribution, "USD")}, earnings {money(aqee.box2_earnings, "USD")}</span>
                {aqee.tax_free_earnings != null && <span className="text-[#077A39]">Tax-free: {money(aqee.tax_free_earnings, "USD")}</span>}
                {showTaxable && <span className="font-bold text-[#F01919]">Taxable: {money(aqee.taxable_earnings, "USD")}</span>}
              </div>
            )}
            {showTaxable && aqee.scholarship_penalty_exception && (
              <div className="mt-1.5 flex items-start gap-1.5 rounded-md border border-[#D97706]/30 bg-[#FEF3C7] px-3 py-1.5 text-[11px] text-[#92400E]" data-testid={`scholarship-penalty-note-${student.id}`}>
                <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
                The 10% additional tax is waived up to the scholarship amount (IRS Pub 970 ch. 7, exceptions); regular income tax still applies.
              </div>
            )}
            {showTaxable && aqee.aotc_penalty_exception && (
              <div className="mt-1.5 flex items-start gap-1.5 rounded-md border border-[#034AA2]/30 bg-[#F0F6FF] px-3 py-1.5 text-[11px] text-[#034AA2]" data-testid={`aotc-penalty-note-${student.id}`}>
                <Info size={14} weight="bold" className="mt-0.5 shrink-0" />
                The taxable slice created by the AOTC reservation is exempt from the 10% additional tax (IRS Pub 970 ch. 7, exception 5); regular income tax still applies.
              </div>
            )}
            {(aqee.cross_checks || []).map((cc, i) => (
              <div key={i} className="mt-1.5 flex items-start gap-1.5 rounded-md border border-[#D97706]/30 bg-[#FEF3C7] px-3 py-1.5 text-[11px] text-[#92400E]" data-testid={`cross-check-${cc.type}-${student.id}`}>
                <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
                {cc.message}
              </div>
            ))}
            <div className="mt-1 text-[10px] text-[#9CA3AF]">{aqee.citation} <a href="/tax-forms" className="text-[#034AA2] hover:underline">Enter 1099-Q / 1098-T / AOTC in Tax Forms & Audit →</a></div>
          </div>
        );
      })}
    </div>
  );
}


function ScholarshipDialog({ open, onClose, editing, students, defaultStudent, onSaved }) {
  // One key for this opening, sent with every press of Save in it, so a
  // second press records nothing new. (ZEN-759; lib/saveOnce.js)
  const saveKey = useSaveKey(`${open}:${editing?.id || "new"}`);
  const [f, setF] = useState({});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const { onDateValidation, hasDateErrors } = useDateErrors();

  useFillOnOpen(open, editing?.id || "new", () => {
    setF(editing ? { ...editing } : {
      student_id: defaultStudent || "",
      tax_year: new Date().getFullYear(),
      label: "", source_type: "scholarship", amount: "",
      date_received: "", tax_free: true, notes: "",
    });
  });

  const save = async () => {
    if (hasDateErrors()) { toast.error("Fix the date errors before saving."); return; }
    if (!f.student_id) { toast.error("Select a beneficiary"); return; }
    if (!f.label) { toast.error("Enter a label"); return; }
    if (!f.amount) { toast.error("Enter an amount"); return; }
    const body = { ...f, amount: parseFloat(f.amount), tax_year: parseInt(f.tax_year) };
    try {
      if (editing) await api.put(`/scholarships/${editing.id}`, body);
      else await api.post("/scholarships", body, withSaveKey(saveKey));
      toast.success(editing ? "Updated" : "Scholarship saved");
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Edit scholarship/aid" : "Add scholarship/aid"}</DialogTitle><DialogDescription>Tax-free assistance reduces AQEE per IRS Pub 970.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <L label="Beneficiary" full>
            <select className="inp" value={f.student_id || ""} onChange={(e) => set("student_id", e.target.value)} data-testid="scholarship-student">
              <option value="">Choose a beneficiary…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </L>
          <L label="Tax year">
            <input type="number" className="inp" value={f.tax_year || ""} onChange={(e) => set("tax_year", e.target.value)} data-testid="scholarship-tax-year" />
          </L>
          <L label="Label" full>
            <input className="inp" value={f.label || ""} onChange={(e) => set("label", e.target.value)} placeholder="e.g., Merit Scholarship - State U" data-testid="scholarship-label" />
          </L>
          <L label="Source type">
            <select className="inp" value={f.source_type || "scholarship"} onChange={(e) => set("source_type", e.target.value)} data-testid="scholarship-source-type">
              {SOURCE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </L>
          <L label="Amount">
            <MoneyInput currency="USD" value={f.amount ?? ""} onChange={(v) => set("amount", v)} testid="scholarship-amount" />
          </L>
          <L label="Date received (optional)">
            <UnifiedDateInput value={f.date_received || ""} onChange={(v) => set("date_received", v)} testid="scholarship-date" preset="softFutureDate" onValidation={onDateValidation("date_received")} />
          </L>
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-sm font-semibold" data-testid="scholarship-tax-free-label">
              <input type="checkbox" checked={f.tax_free !== false} onChange={(e) => set("tax_free", e.target.checked)} data-testid="scholarship-tax-free" />
              Tax-free (reduces AQEE)
            </label>
            <p className="mt-1 text-[11px] text-[#6b7280]">Only tax-free amounts reduce AQEE. Uncheck if this portion was used for non-qualified expenses like room &amp; board.</p>
          </div>
          <NotesField className="col-span-2" value={f.notes} onChange={(v) => set("notes", v)} testid="scholarship-notes" />
        </div>
        <DialogFooter><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn enterPrimary variant="green" onClick={save} disabled={!f.student_id} data-testid="scholarship-save">Save</Btn></DialogFooter>
        <style>{`.inp{height:40px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 8px;font-size:14px;background:#fff}`}</style>
      </DialogContent>
    </Dialog>
  );
}

function L({ label, children, full }) { return <label className={`block ${full ? "col-span-2" : ""}`}><span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">{label}</span>{children}</label>; }

